import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  ScrollView,
  TouchableOpacity,
  Text,
  ActivityIndicator,
  TextInput,
  Modal,
  Platform,
  Dimensions,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useSelector, useDispatch } from 'react-redux';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import AuditHistory from '../../ActionPlan/components/AuditHistory';
import LHModalPop from '../../../components/LHModalPop';
import { useActionPlanDetails } from '../../ActionPlan/hooks/useActionPlanDetails';
import { actionPlanDetailsStyles as styles } from '../../ActionPlan/styles/actionPlanDetails.styles';
import { ActionPlanDetailsProps } from '../../ActionPlan/types';
import { RootState, AppDispatch } from '../../../store';
import { createApiClient, apiClient, getTimezoneOffsetString } from '../../../api/apiClient';
import { setToastNotification } from '../../../store/slices/standUpSlice';
import { fetchActionPlanById } from '../../ActionPlan/services/actionPlanService';
import { refetchNotifications } from '../../../store/slices/notificationSlice';
import CreateStandUpActionPlanForm from './CreateStandUpActionPlan';
import { useToast } from '../../../components/LhToastContainer';
import { handleStandUpErrorWithFriendlyMessage } from '../../../store/utils/standUpErrorHandler';
import { STANDUP_API_NAMES } from '../../../store/slices/standUpSlice';

// Role names that have full edit access (can edit any action plan)
const FULL_EDIT_ACCESS_ROLES = ['Super User', 'COE', 'Region Leader/Division'];

// Role names that need location matching for edit access
const LOCATION_MATCHING_EDIT_ROLES = ['Site Leader'];

// Role names that need owner/assignee matching for edit access
const OWNER_ASSIGNEE_MATCHING_EDIT_ROLES = ['Site Supervisor', 'Certifier', 'Inventory Coordinator', 'Team Lead', 'Checker'];

/**
 * Utility function to check if user can delete an action plan based on role-based permissions
 * Delete Permission Rules:
 * - Super User: Can delete action plans owned by ANY role
 * - COE: Can delete ONLY if owner is COE (NOT Super User or Region Leader/Division)
 * - Region Leader/Division: Can delete ONLY if owner is Region Leader/Division, Site Leader, Site Supervisor, Certifier, Inventory Coordinator, Team Lead, or Checker (NOT Super User or COE)
 * - Site Leader: Can delete if owner is Site Leader or certain frontline roles (Site Supervisor, Certifier, Inventory Coordinator, Team Lead, Checker)
 * - Site Supervisor, Certifier, Inventory Coordinator, Team Lead, Checker: CANNOT delete ANY action plan
 */
const checkCanDeleteActionPlan = (
  userRoleName: string | undefined,
  actionPlanOwnerRole: string | undefined
): boolean => {
  // If no role name, deny access
  if (!userRoleName) return false;

  // Normalize roles to handle whitespace and case differences
  const normalizedUserRole = String(userRoleName || '').trim();
  const normalizedOwnerRole = String(actionPlanOwnerRole || '').trim();

  // Super User can delete action plans owned by anyone
  if (normalizedUserRole === 'Super User') {
    return true;
  }

  // COE can only delete if owner is COE
  if (normalizedUserRole === 'COE') {
    // COE may delete action plans created by COE and several frontline roles
    const allowedForCOE = [
      'COE',
      'Site Leader',
      'Site Supervisor',
      'Certifier',
      'Inventory Coordinator',
      'Team Lead',
      'Checker',
    ];
    return allowedForCOE.includes(normalizedOwnerRole);
  }

  // Region Leader/Division can only delete if owner is Region Leader/Division, Site Leader, Site Supervisor, Certifier, Inventory Coordinator, Team Lead, or Checker
  if (normalizedUserRole === 'Region Leader/Division') {
    // Region Leader/Division may delete action plans created by their level and several frontline roles
    const allowedForRegionLeader = [
      'Region Leader/Division',
      'Site Leader',
      'Site Supervisor',
      'Certifier',
      'Inventory Coordinator',
      'Team Lead',
      'Checker',
    ];
    return allowedForRegionLeader.includes(normalizedOwnerRole);
  }

  // Site Leader can delete if owner is Site Leader or certain frontline roles
  if (normalizedUserRole === 'Site Leader') {
    const allowedForSiteLeader = [
      'Site Leader',
      'Site Supervisor',
      'Certifier',
      'Inventory Coordinator',
      'Team Lead',
      'Checker',
    ];
    return allowedForSiteLeader.includes(normalizedOwnerRole);
  }

  // Site Supervisor, Certifier, Inventory Coordinator, Team Lead, Checker cannot delete any action plan
  if (['Site Supervisor', 'Certifier', 'Inventory Coordinator', 'Team Lead', 'Checker'].includes(normalizedUserRole)) {
    return false;
  }

  // Default: deny access for other roles
  return false;
};

/**
 * Utility function to check if user can edit an action plan based on role-based permissions
 * @param userRoleName - The user's role name
 * @param userGpid - The user's GPID
 * @param userLocations - Array of location names the user has access to
 * @param actionPlanOwnerGpid - The action plan owner's GPID
 * @param actionPlanAssigneeGpid - The action plan assignee's GPID
 * @param actionPlanLocation - The action plan's location
 * @returns boolean - Whether the user can edit the action plan
 */
const checkCanEditActionPlan = (
  userRoleName: string | undefined,
  userGpid: string | undefined,
  userLocations: string[] | undefined,
  actionPlanOwnerGpid: string | undefined,
  actionPlanAssigneeGpid: string | undefined,
  actionPlanLocation: string | undefined
): boolean => {
  // If no role name, deny access
  if (!userRoleName) return false;

  // Full access roles can edit any action plan
  if (FULL_EDIT_ACCESS_ROLES.includes(userRoleName)) {
    return true;
  }

  // Site Leader role needs location matching - can edit if user has access to the action plan's location
  if (LOCATION_MATCHING_EDIT_ROLES.includes(userRoleName)) {
    if (!userLocations || userLocations.length === 0 || !actionPlanLocation) return false;
    const normalizedActionPlanLocation = actionPlanLocation.toLowerCase().trim();
    return userLocations.some(loc => loc.toLowerCase().trim() === normalizedActionPlanLocation);
  }

  // Other roles (Site Supervisor, Certifier, etc.) need to be owner or assignee
  if (OWNER_ASSIGNEE_MATCHING_EDIT_ROLES.includes(userRoleName)) {
    if (!userGpid) return false;
    const normalizedUserGpid = String(userGpid).toLowerCase().trim();
    const normalizedOwnerGpid = String(actionPlanOwnerGpid || '').toLowerCase().trim();
    const normalizedAssigneeGpid = String(actionPlanAssigneeGpid || '').toLowerCase().trim();

    return normalizedUserGpid === normalizedOwnerGpid || normalizedUserGpid === normalizedAssigneeGpid;
  }

  // Default: deny access for other roles
  return false;
};

interface TopOpportunityDetailsProps {
  onBackPress: () => void;
  actionPlanId?: string;
  actionPlanData?: any;
  onActionPlanSaved?: (data: any) => void;
  onActionPlanDeleted?: (id: string) => void;
  initialMode?: 'view' | 'edit';
  backButtonText?: string;
  showTopOpportunityToggle?: boolean;
  sessionId?: string;
}

const TopOpportunityDetails: React.FC<TopOpportunityDetailsProps> = ({
  onBackPress,
  actionPlanId,
  actionPlanData: initialActionPlanData,
  onActionPlanSaved,
  onActionPlanDeleted,
  initialMode = 'view',
  backButtonText,
  showTopOpportunityToggle = false,
  sessionId,
}) => {
  // Responsive design
  const { width } = Dimensions.get('window');
  const isTablet = width >= 768;
  const isMobile = !isTablet && Platform.OS !== 'web';

  // Toast notifications
  const toast = useToast();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);
  const [declineReason, setDeclineReason] = useState<string>('');
  const [actionPlanData, setActionPlanData] = useState<any>(initialActionPlanData);
  const [isFetchingActionPlan, setIsFetchingActionPlan] = useState(false);
  const [fetchActionPlanError, setFetchActionPlanError] = useState<string | null>(null);
  const [fetchAttempt, setFetchAttempt] = useState(0);
  const [formSubmitFn, setFormSubmitFn] = useState<(() => Promise<any>) | null>(null);
  const [isAccepting, setIsAccepting] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [showSaveChangesModal, setShowSaveChangesModal] = useState(false);
  const [pendingSaveData, setPendingSaveData] = useState<any>(null);
  const [isSavingAfterConfirm, setIsSavingAfterConfirm] = useState(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  // When a delete completes we set this to true to prevent a racing GET from showing an error toast
  const [suppressErrorToastsAfterDelete, setSuppressErrorToastsAfterDelete] = useState(false);
  // Key to force reloading audit history when actions occur (decline/accept/complete/save)
  const [auditRefreshKey, setAuditRefreshKey] = useState<number>(0);
  // Audit trail modal state for mobile
  const [showAuditTrailModal, setShowAuditTrailModal] = useState(false);
  // Top opportunity toggle state
  const [isTopOpportunity, setIsTopOpportunity] = useState(false);
  const [initialTopOpportunity, setInitialTopOpportunity] = useState(false);
  const [initialAreaValue, setInitialAreaValue] = useState<any>(null);
  const [areaFromHistory, setAreaFromHistory] = useState<string | null>(null);

  // Initialize top opportunity state when component mounts
  useEffect(() => {
    if (actionPlanData) {
      // Check if action plan data has top_opportunity field, otherwise default to true for StandUp navigation
      const initialValue = actionPlanData.TOP_OPPORTUNITY === 1 || actionPlanData.TOP_OPPORTUNITY === true ||
                          (actionPlanData.TOP_OPPORTUNITY === undefined && showTopOpportunityToggle);
      setIsTopOpportunity(initialValue);
      setInitialTopOpportunity(initialValue);
      // Compute initial area value from action plan data (area or first capability.question_title)
      try {
        let areaVal: any = null;
        if (actionPlanData.area) {
          areaVal = Array.isArray(actionPlanData.area) ? (actionPlanData.area[0] ?? null) : actionPlanData.area;
        } else if (Array.isArray(actionPlanData.capabilities) && actionPlanData.capabilities.length > 0 && actionPlanData.capabilities[0]) {
          areaVal = actionPlanData.capabilities[0]?.question_title ?? actionPlanData.capabilities[0]?.area ?? actionPlanData.capabilities[0]?.value ?? actionPlanData.capabilities[0]?.displayText ?? null;
        }
        setInitialAreaValue(areaVal);
      } catch (e) {
        setInitialAreaValue(null);
      }
    }
  }, [actionPlanData]);

  // Update area value when history data provides QUESTION_TITLE
  useEffect(() => {
    // If action plan doesn't have area but history provides QUESTION_TITLE, use it
    if (!initialAreaValue && areaFromHistory) {
      setInitialAreaValue(areaFromHistory);
      console.log('[TopOpportunityDetails] Area populated from history API:', areaFromHistory);
    }
  }, [initialAreaValue, areaFromHistory]);

  // Callback to receive QUESTION_TITLE from AuditHistory component
  const handleQuestionTitleLoaded = useCallback((questionTitle: string | null) => {
    if (questionTitle) {
      setAreaFromHistory(questionTitle);
      console.log('[TopOpportunityDetails] QUESTION_TITLE loaded from history:', questionTitle);
    }
  }, []);

  // Enrich actionPlanData with AREA field from history API if not already present
  const enrichedActionPlanData = React.useMemo(() => {
    console.log('[TopOpportunityDetails] enrichedActionPlanData memo running:', {
      hasActionPlanData: !!actionPlanData,
      'actionPlanData.AREA': actionPlanData?.AREA,
      'actionPlanData.capabilities': actionPlanData?.capabilities,
      initialAreaValue,
      areaFromHistory,
    });
    
    if (!actionPlanData) return actionPlanData;
    
    // If actionPlanData already has AREA, return as-is
    if (actionPlanData.AREA) {
      console.log('[TopOpportunityDetails] Using existing AREA from actionPlanData:', actionPlanData.AREA);
      return actionPlanData;
    }
    
    // If we have area from capabilities, return as-is
    if (actionPlanData.capabilities?.[0]?.question_title) {
      console.log('[TopOpportunityDetails] Using AREA from capabilities:', actionPlanData.capabilities[0].question_title);
      return actionPlanData;
    }
    
    // If we have area from history API, add it to actionPlanData
    if (initialAreaValue) {
      console.log('[TopOpportunityDetails] ✅ Enriching actionPlanData with AREA from history:', initialAreaValue);
      return {
        ...actionPlanData,
        AREA: initialAreaValue,
      };
    }
    
    console.log('[TopOpportunityDetails] ⚠️ No AREA found - returning actionPlanData as-is');
    return actionPlanData;
  }, [actionPlanData, initialAreaValue, areaFromHistory]);

  // Redux selectors and dispatch
  const dispatch = useDispatch<AppDispatch>();
  const userGpid = useSelector((state: RootState) => state.appData?.userInfo?.GPID);
  const userRoleName = useSelector((state: RootState) => state.appData?.userInfo?.roleName);
  const userEmail = useSelector((state: RootState) => state.appData?.userInfo?.email ?? state.auth?.user?.username);
  const standupId = useSelector((state: RootState) => state.standupRedirect?.session?.standupId);
  const userRoleId = useSelector((state: RootState) => state.appData?.userInfo?.roleId ?? state.auth?.user?.roleId ?? null);
  // Get user's locations from the locations array
  const userLocations = useSelector((state: RootState) =>
    state.appData?.locations?.map((loc: any) => loc.name || loc.displayText) || []
  );

  // Determine button visibility based on user role and action plan status
  const isAssignee = String(userGpid) === String(actionPlanData?.ASSIGNEE_GPID);
  const isOwner = String(userGpid) === String(actionPlanData?.OWNER_GPID);
  const assignmentStatus = actionPlanData?.ASSIGNMENT_STATUS;
  const actionPlanStatus = actionPlanData?.STATUS;

  // Determine which buttons to show
  const showAssigneeActions = isAssignee;
  const showOwnerActions = isOwner && !isAssignee;
  const showAcceptDeclineButton = actionPlanData?.OWNER_GPID !== actionPlanData?.ASSIGNEE_GPID;

  // Normalize roles for comparisons
  const assigneeRole = String(actionPlanData?.ASSIGNEE_ROLE || '').trim();
  const ownerRole = String(actionPlanData?.OWNER_ROLE || '').trim();

  // Lower-cased role strings for comparisons
  const assigneeRoleLower = assigneeRole.toLowerCase();
  const ownerRoleLower = ownerRole.toLowerCase();

  // Normalize assignment status for case-insensitive comparison
  const assignmentStatusLower = String(assignmentStatus || '').toLowerCase();

  const isOwnerSuperUser = ownerRoleLower === 'super user';
  const isOwnerCOE = ownerRoleLower === 'coe';
  const isOwnerRegionLeader = ownerRoleLower === 'region leader/division' || ownerRoleLower === 'region leader';
  const isOwnerSiteLeader = ownerRoleLower === 'site leader';
  const isOwnerSiteSupervisor = ownerRoleLower === 'site supervisor';
  const isOwnerTeamLead = ownerRoleLower === 'team lead';
  const isOwnerChecker = ownerRoleLower === 'checker';
  const isOwnerCertifier = ownerRoleLower === 'certifier';
  const isOwnerInventoryCoordinator = ownerRoleLower === 'inventory coordinator';

  // When owner is Super User, only Super User assignee is allowed to Accept/Decline
  const allowedAssigneeRolesWhenOwnerSuperUser = [
    'super user',
  ];

  // When owner is COE, only these assignee roles are allowed to Accept/Decline
  const allowedAssigneeRolesWhenOwnerCOE = [
    'super user',
    'coe',
    'region leader/division',
    'region leader',
  ];

  // When owner is Region Leader/Division, only these assignee roles are allowed to Accept/Decline
  const allowedAssigneeRolesWhenOwnerRegionLeader = [
    'super user',
    'coe',
    'region leader/division',
    'region leader',
  ];

  // When owner is Site Leader, only these assignee roles are allowed to Accept/Decline
  const allowedAssigneeRolesWhenOwnerSiteLeader = [
    'super user',
    'coe',
    'region leader/division',
    'region leader',
    'site leader',
  ];

  // When owner is Site Supervisor, only these assignee roles are allowed to Accept/Decline
  const allowedAssigneeRolesWhenOwnerSiteSupervisor = [
    'super user',
    'coe',
    'region leader/division',
    'region leader',
    'site leader',
    'site supervisor',
  ];

  // When owner is Team Lead, only these assignee roles are allowed to Accept/Decline
  const allowedAssigneeRolesWhenOwnerTeamLead = [
    'super user',
    'coe',
    'region leader/division',
    'region leader',
    'site leader',
    'site supervisor',
    'certifier',
    'inventory coordinator',
  ];

  // When owner is Checker, only these assignee roles are allowed to Accept/Decline
  const allowedAssigneeRolesWhenOwnerChecker = [
    'super user',
    'coe',
    'region leader/division',
    'region leader',
    'site leader',
    'site supervisor',
    'certifier',
    'inventory coordinator',
  ];

  // When owner is Certifier, only these assignee roles are allowed to Accept/Decline
  const allowedAssigneeRolesWhenOwnerCertifier = [
    'super user',
    'coe',
    'region leader/division',
    'region leader',
    'site leader',
    'site supervisor',
    'certifier',
    'inventory coordinator',
  ];

  // When owner is Inventory Coordinator, only these assignee roles are allowed to Accept/Decline
  const allowedAssigneeRolesWhenOwnerInventoryCoordinator = [
    'super user',
    'coe',
    'region leader/division',
    'region leader',
    'site leader',
    'site supervisor',
    'certifier',
    'inventory coordinator',
  ];

  // Show Accept/Decline only for the assignee when assignment status is 'assigned'
  // Apply additional owner-role based rules:
  // - If owner is Super User: hide buttons when assignee role is in forbiddenAssigneeRolesWhenOwnerSuperUser
  // - If owner is COE: show buttons only when assignee role is in allowedAssigneeRolesWhenOwnerCOE
  // - If owner is Region Leader/Division: show buttons only when assignee role is in allowedAssigneeRolesWhenOwnerRegionLeader
  // - If owner is Site Leader: show buttons only when assignee role is in allowedAssigneeRolesWhenOwnerSiteLeader
  // - If owner is Site Supervisor: show buttons only when assignee role is in allowedAssigneeRolesWhenOwnerSiteSupervisor
  // - If owner is Team Lead: show buttons only when assignee role is in allowedAssigneeRolesWhenOwnerTeamLead
  // - If owner is Checker: show buttons only when assignee role is in allowedAssigneeRolesWhenOwnerChecker
  // - If owner is Certifier: show buttons only when assignee role is in allowedAssigneeRolesWhenOwnerCertifier
  // - If owner is Inventory Coordinator: show buttons only when assignee role is in allowedAssigneeRolesWhenOwnerInventoryCoordinator
  // - Otherwise: default to showing the buttons for the assignee when status is 'assigned' or 'pending'
  let showAcceptDeclineButtons = false;
  // Show Accept/Decline for both 'assigned' and 'pending' statuses (pending = newly assigned, waiting for assignee action)
  if (isAssignee && (assignmentStatusLower === 'assigned' || assignmentStatusLower === 'pending')) {
    if (isOwnerSuperUser) {
      showAcceptDeclineButtons = allowedAssigneeRolesWhenOwnerSuperUser.includes(assigneeRoleLower) && showAcceptDeclineButton;
    } else if (isOwnerCOE) {
      showAcceptDeclineButtons = allowedAssigneeRolesWhenOwnerCOE.includes(assigneeRoleLower) && showAcceptDeclineButton;
    } else if (isOwnerRegionLeader) {
      showAcceptDeclineButtons = allowedAssigneeRolesWhenOwnerRegionLeader.includes(assigneeRoleLower) && showAcceptDeclineButton;
    } else if (isOwnerSiteLeader) {
      showAcceptDeclineButtons = allowedAssigneeRolesWhenOwnerSiteLeader.includes(assigneeRoleLower) && showAcceptDeclineButton;
    } else if (isOwnerSiteSupervisor) {
      showAcceptDeclineButtons = allowedAssigneeRolesWhenOwnerSiteSupervisor.includes(assigneeRoleLower) && showAcceptDeclineButton;
    } else if (isOwnerTeamLead) {
      showAcceptDeclineButtons = allowedAssigneeRolesWhenOwnerTeamLead.includes(assigneeRoleLower) && showAcceptDeclineButton;
    } else if (isOwnerChecker) {
      showAcceptDeclineButtons = allowedAssigneeRolesWhenOwnerChecker.includes(assigneeRoleLower) && showAcceptDeclineButton;
    } else if (isOwnerCertifier) {
      showAcceptDeclineButtons = allowedAssigneeRolesWhenOwnerCertifier.includes(assigneeRoleLower) && showAcceptDeclineButton;
    } else if (isOwnerInventoryCoordinator) {
      showAcceptDeclineButtons = allowedAssigneeRolesWhenOwnerInventoryCoordinator.includes(assigneeRoleLower) && showAcceptDeclineButton;
    } else {
      showAcceptDeclineButtons = true;
    }
  }

  const showCompleteButton = isAssignee && assignmentStatusLower === 'accepted';

  const showDeleteEditButtons = checkCanEditActionPlan(userRoleName, userGpid, userLocations, actionPlanData?.OWNER_GPID, actionPlanData?.ASSIGNEE_GPID, actionPlanData?.LOCATION);
  // Show StandUp-specific edit button only when navigating from StandUp screen
  const showStandUpEditButton = backButtonText === 'Back to Top Opportunities Dashboard';
  const canDeleteActionPlan = checkCanDeleteActionPlan(userRoleName, actionPlanData?.OWNER_ROLE);
  const isCompleted = actionPlanStatus === 'completed';

  const handleActionPlanUpdated = (updatedData: any) => {
    setActionPlanData(updatedData);
    // Notify parent component about the update for list refresh
    if (onActionPlanSaved) {
      onActionPlanSaved(updatedData);
    }

    // Force audit history to refresh immediately so "Updated" appears without manual refresh
    setAuditRefreshKey((k) => k + 1);
  };

  // Store the form's submit function when edit mode starts
  // Memoize so the callback identity is stable when passed to the child form
  const handleFormSubmitReady = useCallback((submitFn: () => Promise<any>) => {
    setFormSubmitFn(() => submitFn);
  }, []);

  // Fetch the latest action plan data when component mounts or when actionPlanId changes
  // Skip fetch if actionPlanData is already provided (from parent component)
  useEffect(() => {
    let isMounted = true;

    const fetchActionPlanData = async () => {
      if (!actionPlanId || initialActionPlanData) return;

      setIsFetchingActionPlan(true);
      setFetchActionPlanError(null);

      try {
        console.log('Fetching action plan data for ID (fresh):', actionPlanId);
        const response = await fetchActionPlanById(actionPlanId, { isTemp: false });

        if (isMounted && response?.status === 'success' && response?.data) {
          console.log('Action plan data fetched (fresh):', response.data);
          setActionPlanData(response.data);
        }
      } catch (error: any) {
        console.error('Error fetching action plan data:', error);

        if (!isMounted) return;

        const statusCode = error?.response?.status;
        if (statusCode === 404) {
          console.log('Action plan not found (likely deleted) - suppressing error toast.');
          setFetchActionPlanError('Action plan not found.');
          return;
        }
        if (statusCode === 429) {
          console.log('Rate limited on action plan fetch - suppressing error toast.');
          setFetchActionPlanError('Too many requests. Please try again.');
          return;
        }

        setFetchActionPlanError('Failed to load action plan details.');
        handleStandUpErrorWithFriendlyMessage(
          STANDUP_API_NAMES.FETCH_ACTION_PLAN,
          error,
          true,
          { actionPlanId }
        );
      } finally {
        if (isMounted) setIsFetchingActionPlan(false);
      }
    };

    if (actionPlanId && !initialActionPlanData) {
      fetchActionPlanData();
    }

    return () => { isMounted = false; };
  }, [actionPlanId, fetchAttempt, initialActionPlanData]);

  const formRehydrateKey = `${actionPlanId ?? 'unknown'}-${actionPlanData ? 'loaded' : 'loading'}`;

  const {
    isEditMode,
    formData,
    handleDownloadPDF,
    isDownloadingPDF,
    handleEditActionPlan,
    handleCancelEdit,
    handleSaveEdit: originalHandleSaveEdit,
    isSaving,
  } = useActionPlanDetails(actionPlanData, actionPlanId, handleActionPlanUpdated, initialMode, showTopOpportunityToggle = true, isTopOpportunity, initialTopOpportunity, undefined, undefined, sessionId);

  // Wrap handleSaveEdit to show modal first, then make API call on confirmation
  const handleSaveEdit = async (data: any) => {
    // If top opportunity was toggled in this form, mark the payload so
    // the PUT call will include TOP_OPPORTUNITY only when intended.
    const payloadToSave = { ...data } as any;
    if (isTopOpportunity !== initialTopOpportunity) {
      // Attach explicit value and a marker so the hook will send it
      payloadToSave.TOP_OPPORTUNITY = isTopOpportunity ? true : false;
      payloadToSave.__sendTopOpportunity = true;
    }
    // If the form included capabilities (area) as modified fields, mark it so
    // the API hook will include `capabilities` only when intended.
    if (data && data.capabilities !== undefined) {
      payloadToSave.__sendArea = true;
    }

    // Store the data and show modal - don't make API call yet
    setPendingSaveData(payloadToSave);
    setShowSaveChangesModal(true);
  };

  // Handle the actual save when user clicks "Continue" in the modal
  const handleConfirmSave = async () => {
    if (!pendingSaveData) return;

    setIsSavingAfterConfirm(true);
    try {
      const result = await originalHandleSaveEdit(pendingSaveData);
      if (result?.success) {
        setShowSaveChangesModal(false);
        setPendingSaveData(null);

        // Show success notification for Top Opportunity context
        toast.showToast({
          header: '',
          subtext: 'Action plan has been updated',
          variant: 'success',
        });

        // Trigger immediate notifications refresh after save
        try {
          dispatch(refetchNotifications());
        } catch (e) {
          console.warn('Failed to trigger notifications refetch after save:', e);
        }
      } else if (result?.error) {
        // Show overlay modal for update action plan error in StandUp
        handleStandUpErrorWithFriendlyMessage(
          STANDUP_API_NAMES.UPDATE_ACTION_PLAN,
          new Error(result.error),
          true,  // displayModal=true
          { actionPlanId }
        );
      }
    } catch (error: any) {
      // Catch any unexpected errors and show overlay modal
      console.error('Unexpected error during action plan save:', error);
      handleStandUpErrorWithFriendlyMessage(
        STANDUP_API_NAMES.UPDATE_ACTION_PLAN,
        error,
        true,  // displayModal=true
        { actionPlanId }
      );
    } finally {
      setIsSavingAfterConfirm(false);
    }
  };

  // Handle cancel in modal
  const handleCancelSaveModal = () => {
    setShowSaveChangesModal(false);
    setPendingSaveData(null);
  };

  const handleCancelEditWithWarning = () => {
    if (isEditMode) {
      setShowUnsavedChangesModal(true);
    }
  };

  const handleConfirmUnsavedChanges = () => {
    setShowUnsavedChangesModal(false);
    handleCancelEdit();
  };

  const handleCancelUnsavedChanges = () => {
    setShowUnsavedChangesModal(false);
  };

  const handleDeletePress = () => {
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {

      const gpidToUse = String(userGpid || 'unknown');
      const roleIdToUse = String(userRoleId || 'unknown');
      const standupIdParam = standupId ? `&standup_id=${encodeURIComponent(standupId)}` : '';
      const sessionIdParam = sessionId ? `&session_id=${encodeURIComponent(sessionId)}` : '';
      const isTemp = actionPlanData?.IS_TEMP ?? actionPlanData?.is_temp === 1 ? `&is_temp=true` : '&is_temp=false';
      const api = createApiClient();
      const deleteUrl = `/action-plans/${actionPlanId}?deleted_by=${encodeURIComponent(gpidToUse)}&deleted_by_role_id=${encodeURIComponent(roleIdToUse)}${standupIdParam}${sessionIdParam}${isTemp}`;
      console.log('Delete API URL:', deleteUrl);

      // Make API call to delete the action plan
      const response = await apiClient(
        api,
        deleteUrl,
        'DELETE'
      );

      console.log('Action Plan deletion response:', response);

      // Dispatch success toast to Redux (persists across navigation)
      dispatch(setToastNotification({
        header: '',
        subtext: 'Action plan has been deleted',
        variant: 'success',
        position: 'top',
        duration: 4000,
      }));

      setShowDeleteModal(false);
      // Set the suppress flag so any racing GET won't show an error toast
      setSuppressErrorToastsAfterDelete(true);
      // Notify parent/dashboard so it can remove the item immediately
      if (onActionPlanDeleted) {
        try {
          onActionPlanDeleted(String(actionPlanId));
        } catch (e) {
          console.warn('onActionPlanDeleted handler threw:', e);
        }
      }
      onBackPress(); // Navigate back to list

      // Trigger immediate notifications refresh
      try {
        dispatch(refetchNotifications());
      } catch (e) {
        console.warn('Failed to trigger notifications refetch after delete:', e);
      }

      // Reset suppress flag after short delay so future errors still surface
      setTimeout(() => setSuppressErrorToastsAfterDelete(false), 2500);
    } catch (error: any) {
      console.error('Error deleting action plan:', error);
      console.error('Full error object:', JSON.stringify(error, null, 2));

      let errorMessage = 'Failed to delete Action Plan';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      // Dispatch error toast to Redux
      dispatch(setToastNotification({
        header: 'Error',
        subtext: errorMessage,
        variant: 'error',
        position: 'top',
        duration: 4000,
      }));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
  };

  const handleDeclinePress = () => {
    setShowDeclineModal(true);
  };

  const handleConfirmDecline = async () => {
    setIsDeclining(true);
    try {
      // Use the ASSIGNEE_GPID from the action plan data, not the current user's GPID
      const assigneeGpid = actionPlanData?.ASSIGNEE_GPID || String(userGpid || 'unknown');
      const assigneeRoleId = actionPlanData?.ASSIGNEE_ROLE_ID;
      const isTemp = actionPlanData?.IS_TEMP ?? actionPlanData?.is_temp === 1 ? true : false;
      const assigneeEmail = actionPlanData?.ASSIGNEE_EMAIL || userEmail || '';

      const api = createApiClient();
      

      // Prepare the request payload according to API spec
      const declinePayload = {
        assignee_gpid: assigneeGpid,
        assignee_role_id: assigneeRoleId,
        assignee_email: assigneeEmail,
        session_id:encodeURIComponent(sessionId || ''),
        standup_id:encodeURIComponent(standupId || ''),
        is_temp:isTemp,
        notes: declineReason || 'No reason provided',
      };

      console.log('Decline API Payload:', declinePayload);

      // Make API call to decline the action plan
      const response = await apiClient(
        api,
        `/action-plans/${actionPlanId}/decline`,
        'POST',
        declinePayload
      );

      console.log('Action Plan decline response:', response);

      if (response?.data) {
        const updatedData = {
          ...actionPlanData,
          ...response.data,
          ASSIGNMENT_STATUS: 'declined', // Explicitly set declined status
        };
        setActionPlanData(updatedData);
        if (onActionPlanSaved) {
          onActionPlanSaved(updatedData);
        }

        setAuditRefreshKey((k) => k + 1);
      } else {
        const updatedData = {
          ...actionPlanData,
          ASSIGNMENT_STATUS: 'declined',
        };
        setActionPlanData(updatedData);
        if (onActionPlanSaved) {
          onActionPlanSaved(updatedData);
        }
        setAuditRefreshKey((k) => k + 1);
      }


      dispatch(setToastNotification({
        header: '',
        subtext: 'The Action Plan has been declined',
        variant: 'warning',
        position: 'top',
        duration: 4000,
      }));

      setShowDeclineModal(false);
      setDeclineReason(''); // Reset the reason field

      // Trigger immediate notifications refresh
      try {
        dispatch(refetchNotifications());
      } catch (e) {
        console.warn('Failed to trigger notifications refetch after decline:', e);
      }


      if (actionPlanId) {
        try {
          const api = createApiClient();
          const freshResponse = await apiClient(
            api,
            `/action-plans/${actionPlanId}`,
            'GET'
          );

          if (freshResponse?.status === 'success' && freshResponse?.data) {
            setActionPlanData(freshResponse.data);
            if (onActionPlanSaved) {
              onActionPlanSaved(freshResponse.data);
            }
          }
        } catch (error) {
          console.warn('Could not fetch fresh action plan data after decline:', error);

        }
      }
    } catch (error: any) {
      console.error('Error declining action plan:', error);
      console.error('Full error object:', JSON.stringify(error, null, 2));

      handleStandUpErrorWithFriendlyMessage(
        STANDUP_API_NAMES.DECLINE_ACTION_PLAN,
        error,
        true,
        { actionPlanId }
      );
    } finally {
      setIsDeclining(false);
    }
  };

  const handleCancelDecline = () => {
    setShowDeclineModal(false);
  };

  const handleAcceptPress = async () => {
    setIsAccepting(true);
    try {
      const assigneeGpid = actionPlanData?.ASSIGNEE_GPID || String(userGpid || 'unknown');
      const assigneeRoleId = actionPlanData?.ASSIGNEE_ROLE_ID;
      const assigneeEmail = actionPlanData?.ASSIGNEE_EMAIL || userEmail || '';
      const isTemp = actionPlanData?.IS_TEMP ?? actionPlanData?.is_temp === 1 ? true : false;
      const api = createApiClient();

      const acceptPayload = {
        assignee_gpid: assigneeGpid,
        assignee_role_id: assigneeRoleId,
        assignee_email: assigneeEmail,
        standup_id:encodeURIComponent(standupId || ''),
        session_id:encodeURIComponent(sessionId || ''),
        is_temp: isTemp,
      };

      console.log('Accept API Payload:', acceptPayload);

      const response = await apiClient(
        api,
        `/action-plans/${actionPlanId}/accept`,
        'POST',
        acceptPayload
      );

      console.log('Action Plan accept response:', response);

      dispatch(setToastNotification({
        header: '',
        subtext: 'The Action Plan has been accepted',
        variant: 'success',
        position: 'top',
        duration: 4000,
      }));

      // Refetch the action plan data to update the UI
      if (actionPlanId) {
        try {
          const api = createApiClient();
          const response = await apiClient(
            api,
            `/action-plans/${actionPlanId}`,
            'GET'
          );

          if (response?.status === 'success' && response?.data) {
            setActionPlanData(response.data);
            if (onActionPlanSaved) {
              onActionPlanSaved(response.data);
            }
            // Force audit history to refresh immediately
            setAuditRefreshKey(k => k + 1);
          }
        } catch (error) {
          console.error('Error refetching action plan data:', error);
        }
      }

      // Trigger immediate notifications refresh after accept
      try {
        dispatch(refetchNotifications());
      } catch (e) {
        console.warn('Failed to trigger notifications refetch after accept:', e);
      }
    } catch (error: any) {
      console.error('Error accepting action plan:', error);

      handleStandUpErrorWithFriendlyMessage(
        STANDUP_API_NAMES.ACCEPT_ACTION_PLAN,
        error,
        true,
        { actionPlanId }
      );
    } finally {
      setIsAccepting(false);
    }
  };

  const handleCompletePress = async () => {
    setIsCompleting(true);
    try {
      const assigneeGpid = actionPlanData?.ASSIGNEE_GPID || String(userGpid || 'unknown');
      const assigneeRoleId = actionPlanData?.ASSIGNEE_ROLE_ID;
      const isTemp = actionPlanData?.IS_TEMP ?? actionPlanData?.is_temp === 1 ? true : false;
      const assigneeEmail = actionPlanData?.ASSIGNEE_EMAIL || userEmail || '';
      const api = createApiClient();

      const completePayload = {
        assignee_gpid: assigneeGpid,
        assignee_role_id: assigneeRoleId,
        assignee_email: assigneeEmail,
        standup_id:encodeURIComponent(standupId || ''),
        session_id:encodeURIComponent(sessionId || ''),
        is_temp: isTemp,
      };

      console.log('Complete API Payload:', completePayload);

      const response = await apiClient(
        api,
        `/action-plans/${actionPlanId}/complete`,
        'POST',
        completePayload
      );

      console.log('Action Plan complete response:', response);


      const updatedData = {
        ...actionPlanData,
        STATUS: 'completed',
      };
      setActionPlanData(updatedData);
      if (onActionPlanSaved) {
        onActionPlanSaved(updatedData);
      }
      // Force audit history to refresh immediately after completing
      setAuditRefreshKey(k => k + 1);

      dispatch(setToastNotification({
        header: '',
        subtext: 'The Action Plan has been completed',
        variant: 'success',
        position: 'top',
        duration: 4000,
      }));

      // Refetch the action plan data to update the UI with complete server response
      if (actionPlanId) {
        try {
          const api = createApiClient();
          const response = await apiClient(
            api,
            `/action-plans/${actionPlanId}`,
            'GET'
          );

          if (response?.status === 'success' && response?.data) {
            setActionPlanData(response.data);
            if (onActionPlanSaved) {
              onActionPlanSaved(response.data);
            }
          }
        } catch (error) {
          console.error('Error refetching action plan data:', error);
        }
      }

      // Trigger immediate notifications refresh after complete
      try {
        dispatch(refetchNotifications());
      } catch (e) {
        console.warn('Failed to trigger notifications refetch after complete:', e);
      }

      // Notify parent and navigate back to dashboard so user sees updated list
      try {
        if (onActionPlanSaved) {
          onActionPlanSaved(updatedData);
        }
      } catch (e) {
        console.warn('onActionPlanSaved threw after complete:', e);
      }

      try {
        if (typeof onBackPress === 'function') {
          onBackPress();
        }
      } catch (e) {
        console.warn('onBackPress threw after complete:', e);
      }
    } catch (error: any) {
      console.error('Error completing action plan:', error);

      handleStandUpErrorWithFriendlyMessage(
        STANDUP_API_NAMES.COMPLETE_ACTION_PLAN,
        error,
        true,
        { actionPlanId }
      );
    } finally {
      setIsCompleting(false);
    }
  };


  return (
      <SafeAreaView style={styles.container} edges={['bottom', 'left', 'right']}>
      <ScrollView
        style={styles.mainScrollView}
        contentContainerStyle={styles.mainScrollContent}
        horizontal={false}
      >
        {/* Header Section */}
        <View style={styles.headerSection}>
          <View style={styles.headerLeft}>
            <TouchableOpacity
              onPress={onBackPress}
              style={[styles.backButton, Platform.OS === 'ios' && { opacity: 0.4 }]}
              disabled={Platform.OS === 'ios'}
            >
              <MaterialIcons name="arrow-back" size={20} color="#005CBC" />
              <Text style={styles.backButtonText}>{backButtonText || 'Back to Top Opportunities Dashboard'}</Text>
            </TouchableOpacity>
           {/* <View style={styles.titleRow}> */}
             <Text style={styles.headerTitle}>Top Opportunity</Text>
           </View>
           
           <View style={styles.headerButtons}>
             {isEditMode ? (
               <>
                 {/* Edit Mode Buttons */}
                 {showDeleteEditButtons && canDeleteActionPlan && (
                   <TouchableOpacity 
                     onPress={handleDeletePress}
                     disabled={isCompleted}
                     style={[styles.tertiaryButton, isCompleted && { opacity: 0.6 }]}
                     accessibilityLabel="Delete"
                   >
                     <MaterialIcons name="delete-forever" size={20} color="#034895" />
                     <Text style={styles.tertiaryButtonText}>Delete</Text>
                   </TouchableOpacity>
                 )}
                 
                 <TouchableOpacity 
                   onPress={handleCancelEditWithWarning} 
                   style={styles.secondaryButton}
                   accessibilityLabel="Cancel"
                 >
                   <Text style={styles.secondaryButtonText}>Cancel</Text>
                 </TouchableOpacity>
                 
                 <TouchableOpacity 
                   onPress={() => formSubmitFn?.()} 
                   style={[styles.primaryButton, isSaving && { opacity: 0.6 }]}
                   accessibilityLabel="Save"
                   disabled={isSaving || !formSubmitFn}
                 >
                   {isSaving ? (
                     <ActivityIndicator size="small" color="#FFFFFF" />
                   ) : (
                     <Text style={styles.primaryButtonText}>Save</Text>
                   )}
                 </TouchableOpacity>
               </>
             ) : (
              <>
                {/* View Mode Buttons */}
                <TouchableOpacity
                  onPress={() => {
                    console.log('PDF Download button pressed');
                    handleDownloadPDF();
                  }}
                  disabled={isDownloadingPDF}
                  style={styles.secondaryButton}
                  accessibilityLabel="Download as PDF"
                >
                  {isDownloadingPDF ? (
                    <ActivityIndicator size="small" color="#005CBC" />
                  ) : (
                    <>
                      <MaterialIcons name="file-download" size={20} color="#005CBC" />
                      <Text style={styles.secondaryButtonText}>Download as PDF</Text>
                    </>
                  )}
                </TouchableOpacity>

                {/* Show Edit button only if user is owner or assignee */}
                {showDeleteEditButtons && (
                  <TouchableOpacity
                    onPress={handleEditActionPlan}
                    disabled={isCompleted}
                    style={[styles.primaryButton, isCompleted && { opacity: 0.6 }]}
                    accessibilityLabel="Edit Action Plan"
                  >
                    <MaterialIcons name="edit" size={20} color="#FFFFFF" />
                    <Text style={styles.primaryButtonText}>Edit Action Plan</Text>
                  </TouchableOpacity>
                )}

                {/* Show Accept and Decline buttons for assignee when status is assigned */}
                {showAcceptDeclineButtons && (
                  <>
                    <TouchableOpacity
                      onPress={handleAcceptPress}
                      disabled={isAccepting}
                      style={[styles.secondaryButton, isAccepting && { opacity: 0.6 }]}
                      accessibilityLabel="Accept Action Plan"
                    >
                      {isAccepting ? (
                        <ActivityIndicator size="small" color="#005CBC" />
                      ) : (
                        <>
                          <MaterialIcons name="check-circle" size={20} color="#005CBC" />
                          <Text style={styles.secondaryButtonText}>Accept</Text>
                        </>
                      )}
                    </TouchableOpacity>

                    <TouchableOpacity
                      onPress={handleDeclinePress}
                      style={styles.tertiaryButton}
                      accessibilityLabel="Decline Action Plan"
                    >
                      <MaterialIcons name="cancel" size={20} color="#034895" />
                      <Text style={styles.tertiaryButtonText}>Decline</Text>
                    </TouchableOpacity>
                  </>
                )}

                {/* Show Complete button for assignee when status is accepted */}
                {showCompleteButton && (
                  <TouchableOpacity
                    onPress={handleCompletePress}
                    disabled={isCompleting || isCompleted}
                    style={[styles.primaryButton, (isCompleting || isCompleted) && { opacity: 0.6 }]}
                    accessibilityLabel="Complete Action Plan"
                  >
                    {isCompleting ? (
                      <ActivityIndicator size="small" color="#FFFFFF" />
                    ) : (
                      <>
                        <MaterialIcons name="check" size={20} color="#FFFFFF" />
                        <Text style={styles.primaryButtonText}>Complete</Text>
                      </>
                    )}
                  </TouchableOpacity>
                )}
              </>
            )}
          </View>
        </View>

        {/* Content Section */}
        <View style={styles.content}>
          <View style={styles.contentWrapper}>
            <View style={styles.contentPlaceholder}>
              {isFetchingActionPlan && !actionPlanData && !initialActionPlanData ? (
                <View style={{ paddingVertical: 24, alignItems: 'center' }}>
                  <ActivityIndicator size="large" color="#005CBC" />
                  <Text style={{ marginTop: 12, color: '#2D2D2D' }}>Loading action plan…</Text>
                </View>
              ) : fetchActionPlanError && !actionPlanData && !initialActionPlanData ? (
                <View style={{ paddingVertical: 24, alignItems: 'center' }}>
                  <Text style={{ color: '#D40020', textAlign: 'center', marginBottom: 12 }}>
                    {fetchActionPlanError}
                  </Text>
                  <TouchableOpacity
                    onPress={() => setFetchAttempt(a => a + 1)}
                    style={[styles.primaryButton, { alignSelf: 'center' }]}
                    accessibilityLabel="Retry loading action plan"
                  >
                    <Text style={styles.primaryButtonText}>Try again</Text>
                  </TouchableOpacity>
                </View>
              ) : (
                <CreateStandUpActionPlanForm
                  key={formRehydrateKey}
                  data={formData}
                  type={isEditMode ? "edit" : "view"}
                  onSubmit={handleSaveEdit}
                  onCancel={handleCancelEditWithWarning}
                  onFormSubmitReady={handleFormSubmitReady}
                  onViewAuditTrail={() => setShowAuditTrailModal(true)}
                  isMobile={isMobile}
                  actionPlanData={enrichedActionPlanData}
                />
              )}

            </View>

            {/* Audit Trail - Hidden on Mobile, Visible on Web/Tablet */}
            {!isMobile && (
              <View style={styles.auditHistoryPlaceholder}>
                {actionPlanId && (
                  <AuditHistory
                    actionPlanId={actionPlanId}
                    status={actionPlanData?.STATUS || 'Pending'}
                    assignmentStatus={actionPlanData?.ASSIGNMENT_STATUS}
                    createdDate={actionPlanData?.CREATED_AT}
                    createdBy={actionPlanData?.CREATED_BY_NAME || actionPlanData?.CREATED_BY_EMAIL || actionPlanData?.CREATED_BY_GPID}
                    targetDate={actionPlanData?.TARGET_DATE}
                    refreshKey={auditRefreshKey}
                    origin={actionPlanData?.IS_STANDUP || actionPlanData?.is_standup ? 'Stand-up' : undefined}
                    isTemp={actionPlanData?.IS_TEMP ?? actionPlanData?.is_temp}
                    onQuestionTitleLoaded={handleQuestionTitleLoaded}
                  />
                )}
              </View>
            )}
          </View>
        </View>
      </ScrollView>

      {/* Audit Trail Modal - Mobile Only (Bottom Sheet Style) */}
      <Modal
        visible={showAuditTrailModal}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setShowAuditTrailModal(false)}
      >
        <View style={styles.auditTrailModalContainer}>
          <TouchableOpacity
            style={styles.auditTrailModalBackdrop}
            activeOpacity={1}
            onPress={() => setShowAuditTrailModal(false)}
          />
          <View style={styles.auditTrailModalContent}>
            <View style={styles.auditTrailModalHeader}>
              <Text style={styles.auditTrailModalTitle}>Action Plan Audit Trail</Text>
              <TouchableOpacity
                onPress={() => setShowAuditTrailModal(false)}
                style={styles.auditTrailModalCloseButton}
                accessibilityLabel="Close Audit Trail"
                activeOpacity={0.7}
              >
                <MaterialIcons name="close" size={24} color="#333" />
              </TouchableOpacity>
            </View>

            <ScrollView
              style={styles.auditTrailModalScroll}
              showsVerticalScrollIndicator={true}
              bounces={true}
              scrollEventThrottle={16}
              keyboardShouldPersistTaps="handled"
            >
              {actionPlanId && (
                <AuditHistory
                  actionPlanId={actionPlanId}
                  status={actionPlanData?.STATUS || 'Pending'}
                  assignmentStatus={actionPlanData?.ASSIGNMENT_STATUS}
                  createdDate={actionPlanData?.CREATED_AT}
                  createdBy={actionPlanData?.CREATED_BY_NAME || actionPlanData?.CREATED_BY_EMAIL || actionPlanData?.CREATED_BY_GPID}
                  targetDate={actionPlanData?.TARGET_DATE}
                  refreshKey={auditRefreshKey}
                  isModal={true}
                  origin={actionPlanData?.IS_STANDUP || actionPlanData?.is_standup ? 'Stand-up' : undefined}
                  isTemp={actionPlanData?.IS_TEMP ?? actionPlanData?.is_temp}
                />
              )}
            </ScrollView>

            <TouchableOpacity
              onPress={() => setShowAuditTrailModal(false)}
              style={styles.auditTrailModalGoBackButton}
              accessibilityLabel="Go Back"
              activeOpacity={0.8}
            >
              <Text style={styles.auditTrailModalGoBackButtonText}>Go back</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Delete Confirmation Modal */}
      <LHModalPop
        visible={showDeleteModal}
        onClose={handleCancelDelete}
        title="Delete Action Plan"
        description="This will permanently delete the Action Plan"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        onPrimaryPress={handleConfirmDelete}
        onSecondaryPress={handleCancelDelete}
        primaryButtonLoading={isDeleting}
        primaryButtonDisabled={isDeleting}
        showFooter={true}
        dismissOnBackdropPress={false}
      />

      {/* Decline Confirmation Modal */}
      <LHModalPop
        visible={showDeclineModal}
        onClose={handleCancelDecline}
        title="Decline Action Plan"
        description="Are you sure you want to decline the Action Plan?"
        primaryButtonText="Decline"
        secondaryButtonText="Cancel"
        onPrimaryPress={handleConfirmDecline}
        onSecondaryPress={handleCancelDecline}
        primaryButtonLoading={isDeclining}
        primaryButtonDisabled={isDeclining}
        showFooter={true}
        dismissOnBackdropPress={false}
      >
        <View style={{ marginBottom: 16 }}>
          <Text style={{ fontSize: 14, fontWeight: '600', marginBottom: 8, color: '#333' }}>
            Reason for Decline
          </Text>
          <TextInput
            style={{
              borderWidth: 1,
              borderColor: '#ccc',
              borderRadius: 8,
              padding: 12,
              minHeight: 100,
              textAlignVertical: 'top',
              fontSize: 14,
              color: '#333',
            }}
            placeholder="Please provide a reason for declining this action plan..."
            placeholderTextColor="#999"
            multiline={true}
            numberOfLines={4}
            value={declineReason}
            onChangeText={setDeclineReason}
            editable={!isDeclining}
          />
        </View>
      </LHModalPop>

      {/* Save Changes Confirmation Modal */}
      <LHModalPop
        visible={showSaveChangesModal}
        onClose={handleCancelSaveModal}
        title="Save Changes"
        description="Are you sure you want to save changes in the Action Plan?"
        primaryButtonText="Continue"
        secondaryButtonText="Cancel"
        onPrimaryPress={handleConfirmSave}
        onSecondaryPress={handleCancelSaveModal}
        primaryButtonLoading={isSavingAfterConfirm}
        primaryButtonDisabled={isSavingAfterConfirm}
        showFooter={true}
        dismissOnBackdropPress={false}
      />

      {/* Unsaved Changes Confirmation Modal */}
      <LHModalPop
        visible={showUnsavedChangesModal}
        onClose={handleCancelUnsavedChanges}
        title="Unsaved Changes"
        description="Unsaved changes will be lost, Do you want to continue?"
        primaryButtonText="Continue"
        secondaryButtonText="Cancel"
        onPrimaryPress={handleConfirmUnsavedChanges}
        onSecondaryPress={handleCancelUnsavedChanges}
        showFooter={true}
        dismissOnBackdropPress={false}
      />

    </SafeAreaView>
  );
};

export default TopOpportunityDetails;